import { useEffect, useState } from 'react'

/**
 * L'espace de gestion comme une application de téléphone : icône sur l'écran
 * d'accueil, plein écran, ouverture rapide et appel utilisable avec un réseau
 * faible (service worker, portée /admin). Le site public n'est pas concerné.
 */

interface InstallPrompt extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let deferred: InstallPrompt | null = null
const listeners = new Set<() => void>()
// Chrome / Android : la proposition d'installation, gardée pour notre bouton.
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault()
  deferred = e as InstallPrompt
  listeners.forEach((l) => l())
})
window.addEventListener('appinstalled', () => {
  deferred = null
  listeners.forEach((l) => l())
})

/** Ouverte depuis l'écran d'accueil (application installée). */
export const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true

export function setHead(tag: 'link' | 'meta', attrs: Record<string, string>) {
  const key = tag === 'link' ? `link[rel="${attrs.rel}"]` : `meta[name="${attrs.name}"]`
  let el = document.head.querySelector(key)
  if (!el) {
    el = document.createElement(tag)
    document.head.appendChild(el)
  }
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v)
}

/** Déclare l'application (manifeste, icônes) et enregistre le service worker. */
export function useAppShell() {
  useEffect(() => {
    // Ouverte depuis l'application d'un site (CNMY…) : on garde son manifeste
    // et son icône — la gestion fait partie de cette application.
    let siteApp = false
    try {
      siteApp = !!sessionStorage.getItem('publiqa-app-site')
    } catch {
      /* stockage indisponible */
    }
    if (!siteApp) {
      setHead('link', { rel: 'manifest', href: '/manifest.webmanifest' })
      setHead('link', { rel: 'apple-touch-icon', href: '/app-icons/apple-touch-icon.png' })
      setHead('meta', { name: 'apple-mobile-web-app-title', content: 'Publiqa' })
    }
    setHead('meta', { name: 'theme-color', content: '#111318' })
    setHead('meta', { name: 'mobile-web-app-capable', content: 'yes' })
    setHead('meta', { name: 'apple-mobile-web-app-capable', content: 'yes' })
    setHead('meta', { name: 'apple-mobile-web-app-status-bar-style', content: 'default' })
    if ('serviceWorker' in navigator && location.protocol === 'https:')
      navigator.serviceWorker.register('/sw.js', { scope: '/admin' }).catch(() => undefined)
  }, [])
}

/** Données gardées pour le hors-ligne : effacées à la déconnexion (confidentialité). */
export async function clearOfflineData() {
  try {
    const keys = await caches.keys()
    await Promise.all(keys.filter((k) => k.endsWith('-api')).map((k) => caches.delete(k)))
  } catch {
    /* Cache Storage indisponible */
  }
}

/**
 * Proposition d'installation sur téléphone : `show` si l'application n'est
 * pas installée et que le navigateur le permet (bouton direct sur Android,
 * geste à faire dans Safari sur iPhone). Refus mémorisé sous `dismissKey`.
 */
export function useInstall(dismissKey: string) {
  const [, refresh] = useState(0)
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(dismissKey) === '1'
    } catch {
      return false
    }
  })
  useEffect(() => {
    const l = () => refresh((n) => n + 1)
    listeners.add(l)
    return () => {
      listeners.delete(l)
    }
  }, [])
  const ios = /iphone|ipad|ipod/i.test(navigator.userAgent)
  const phone = window.matchMedia('(max-width: 760px)').matches
  const close = () => {
    setDismissed(true)
    try {
      localStorage.setItem(dismissKey, '1')
    } catch {
      /* non mémorisé */
    }
  }
  return {
    show: !dismissed && !isStandalone() && phone && (!!deferred || ios),
    /** Android / Chrome : installation en un geste. */
    direct: !!deferred,
    install: () => {
      void deferred?.prompt()
      void deferred?.userChoice.then(() => close())
    },
    close,
  }
}

/**
 * Bandeau « Installer l'application » (téléphone, pas encore installée) :
 * bouton direct sur Android ; sur iPhone, le geste à faire dans Safari.
 */
export function InstallBanner() {
  const { show, direct, install, close } = useInstall('publiqa-install-dismissed')
  if (!show) return null
  return (
    <div className="a-install" role="note">
      <img src="/app-icons/icon-192.png" alt="" width="40" height="40" />
      <p>
        <strong>Installez Publiqa sur votre téléphone</strong>
        {direct ? (
          <span>Ouverture en un geste, plein écran, appel même avec peu de réseau.</span>
        ) : (
          <span>
            Dans Safari : bouton <b>Partager</b> puis <b>« Sur l’écran d’accueil »</b>.
          </span>
        )}
      </p>
      {direct && (
        <button type="button" className="a-btn a-btn-primary a-btn-sm" onClick={install}>
          Installer
        </button>
      )}
      <button type="button" className="a-icon-btn" aria-label="Plus tard" onClick={close}>
        ✕
      </button>
    </div>
  )
}
